import { usePostExternalIntegrationKeyFactsSettingsMutation } from '@Queries/index';
import * as Api from '@ViewModels';
import {
	MultipleSelectContent,
	MultipleSelectItem,
	MultipleSelectTrigger,
	MultipleSelectValue,
} from '@WebComponents/MultipleSelect';
import { SelectContent, SelectItem, SelectTrigger, SelectValue } from '@WebComponents/Select';
import React from 'react';
import { EditableMultipleSelect, EditableSelect } from '../context';
import { useSearchableFieldFilter } from '../hooks/useSearchableFieldFilter';
import { styleSheet } from '../styles';
import { SettingGroup } from './SettingGroup';
import { SettingRow } from './SettingRow';

interface IKeyFactSettingsGroupProps {
	keyFactSettings: Api.IKeyFactSettings;
	impersonationContext: Api.IImpersonationContext;
	configuration: Api.IIntegrationConfiguration;
	provider: Api.IntegrationProvider;
	setKeyFactsSettings: (keyFactsSettings: Api.IKeyFactSettings) => void;
}

const birthdayDisplayOptions = [
	{ label: 'All', value: Api.BirthdayKeyFactDisplay.All },
	{ label: 'Clients Only', value: Api.BirthdayKeyFactDisplay.ClientOnly },
	{ label: 'Prospects Only', value: Api.BirthdayKeyFactDisplay.ProspectOnly },
];

export const KeyFactSettingsGroup: React.FC<IKeyFactSettingsGroupProps> = ({
	keyFactSettings,
	impersonationContext,
	configuration,
	provider,
	setKeyFactsSettings,
}) => {
	const {
		searchValue: customDateFieldSearchInputValue,
		handleSearchChange: handleCustomDateFieldSearchChange,
		handleSearchClear: handleCustomDateFieldSearchClear,
		filterItems,
	} = useSearchableFieldFilter();

	const isPracticePantherSettings = provider === Api.IntegrationProvider.PracticePanther;
	const ppKeyFactSettings = isPracticePantherSettings ? (keyFactSettings as Api.IPracticePantherKeyFactSettings) : null;

	const isMicrosoftDynamics365SalesSettings = provider === Api.IntegrationProvider.MicrosoftDynamics365Sales;
	const microsoftDynamics365SalesKeyFactSettings = isMicrosoftDynamics365SalesSettings
		? (keyFactSettings as Api.IMicrosoftDynamics365SalesKeyFactSettings)
		: null;
	const microsoftDynamics365SalesConfiguration = isMicrosoftDynamics365SalesSettings
		? (configuration as Api.IMicrosoftDynamics365SalesIntegrationConfiguration)
		: null;

	const isSalesforceSettings = provider === Api.IntegrationProvider.Salesforce;
	const salesforceKeyFactSettings = isSalesforceSettings ? (keyFactSettings as Api.ISalesforceKeyFactSettings) : null;
	const salesforceConfiguration = isSalesforceSettings
		? (configuration as Api.ISalesforceIntegrationConfiguration)
		: null;

	const isServiceTitanSettings = provider === Api.IntegrationProvider.ServiceTitan;
	const serviceTitanKeyFactSettings = isServiceTitanSettings
		? (keyFactSettings as Api.IServiceTitanKeyFactSettings)
		: null;
	const serviceTitanConfiguration = isServiceTitanSettings
		? (configuration as Api.IServiceTitanIntegrationConfiguration)
		: null;

	const postExternalIntegrationKeyFactsSettingsMutation = usePostExternalIntegrationKeyFactsSettingsMutation({
		impersonationContext,
	});

	const onUpdate = (
		updatedSettings:
			| Partial<Api.IMicrosoftDynamics365SalesKeyFactSettings>
			| Partial<Api.ISalesforceKeyFactSettings>
			| Partial<Api.IServiceTitanKeyFactSettings>
			| Partial<Api.IKeyFactSettings>
	) => {
		setKeyFactsSettings({
			...keyFactSettings,
			...updatedSettings,
		});
	};

	const handleCustomFieldChange = (selectedCustomFields: string[]) => {
		onUpdate({ enabledCustomDateFields: selectedCustomFields });
	};

	const handleDateFieldChange = (selectedFields: string[]) => {
		if (isSalesforceSettings && salesforceKeyFactSettings) {
			onUpdate({ enabledDateFields: selectedFields });
		}

		if (isServiceTitanSettings && serviceTitanKeyFactSettings) {
			onUpdate({ enabledDateFields: selectedFields });
		}
	};

	const getCustomDateFieldsDisplay = (
		customFields: string[] = [],
		integrationConfiguration: Partial<Api.IMicrosoftDynamics365SalesIntegrationConfiguration>
	) => {
		if (!customFields?.length) return 'None selected';
		const customFieldsText = getCustomDateFieldsDisplayText(customFields, integrationConfiguration);
		return customFieldsText.join(', ');
	};

	const getCustomDateFieldsDisplayText = (
		customFields: string[] = [],
		integrationConfiguration: Partial<Api.IMicrosoftDynamics365SalesIntegrationConfiguration>
	) => {
		return customFields.map(customFieldId => {
			const text = Object.entries(integrationConfiguration.availableCustomDateFields || {}).find(
				([id, _text]) => id === customFieldId
			)?.[1];
			return text;
		});
	};

	const showBirthdayDisplay = React.useMemo(() => {
		return (
			provider !== Api.IntegrationProvider.MicrosoftDynamics365Sales &&
			provider !== Api.IntegrationProvider.Salesforce &&
			provider !== Api.IntegrationProvider.ServiceTitan
		);
	}, [provider]);

	return (
		<SettingGroup title='Key Facts Settings'>
			{showBirthdayDisplay ? (
				<SettingRow title='Birthday Display' subtitle='Determines type of contact to show birthdays for'>
					<EditableSelect
						items={birthdayDisplayOptions}
						value={keyFactSettings?.birthdayDisplay ?? null}
						onValueChange={(value: string) => onUpdate({ birthdayDisplay: value as Api.BirthdayKeyFactDisplay })}
						displayValue={keyFactSettings?.birthdayDisplay ?? 'Select birthday display'}
					>
						<SelectTrigger styles={styleSheet.editableField}>
							<SelectValue placeholder='Select birthday display' />
						</SelectTrigger>
						<SelectContent>
							{birthdayDisplayOptions.map(option => (
								<SelectItem key={option.value} value={option.value}>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</EditableSelect>
				</SettingRow>
			) : null}

			{isPracticePantherSettings && ppKeyFactSettings && (
				<>
					{/* Now we can safely access PracticePanther-specific settings */}
					{/* <SettingRow title='Custom Key Facts' subtitle={ppSettings.customKeyFactsEnabled ? 'Enabled' : 'Disabled'}>
						<Toggle
							id='customKeyFacts'
							isOn={ppSettings.customKeyFactsEnabled}
							onToggleCheckChanged={checked => onUpdate({ ...ppSettings, customKeyFactsEnabled: checked })}
						/>
					</SettingRow> */}
				</>
			)}

			{isMicrosoftDynamics365SalesSettings && microsoftDynamics365SalesKeyFactSettings ? (
				<>
					<SettingRow title='Custom Fields' subtitle='Select custom date fields to be used as key facts'>
						<EditableMultipleSelect
							value={microsoftDynamics365SalesKeyFactSettings.enabledCustomDateFields || []}
							onChange={handleCustomFieldChange}
							isBusy={postExternalIntegrationKeyFactsSettingsMutation?.isPending}
							displayValue={getCustomDateFieldsDisplay(
								microsoftDynamics365SalesKeyFactSettings.enabledCustomDateFields,
								microsoftDynamics365SalesConfiguration
							)}
						>
							<MultipleSelectTrigger styles={styleSheet.editableField}>
								<MultipleSelectValue
									values={
										getCustomDateFieldsDisplayText(
											microsoftDynamics365SalesKeyFactSettings.enabledCustomDateFields,
											microsoftDynamics365SalesConfiguration
										) || []
									}
									placeholder='Select custom fields'
								/>
							</MultipleSelectTrigger>
							<MultipleSelectContent
								onSearchValueChange={handleCustomDateFieldSearchChange}
								isSearchable={true}
								searchFieldPlaceholder='Search custom fields'
								searchInput={customDateFieldSearchInputValue}
								onSearchClear={handleCustomDateFieldSearchClear}
							>
								{filterItems(
									Object.entries(microsoftDynamics365SalesConfiguration.availableCustomDateFields || {}),
									([_, text]) => text
								).map(([id, text]) => {
									const isSelected = microsoftDynamics365SalesKeyFactSettings.enabledCustomDateFields?.includes(id);
									return (
										<MultipleSelectItem key={id} value={id} selected={isSelected}>
											{text}
										</MultipleSelectItem>
									);
								})}
							</MultipleSelectContent>
						</EditableMultipleSelect>
					</SettingRow>
				</>
			) : null}

			{isServiceTitanSettings && serviceTitanKeyFactSettings ? (
				<>
					<SettingRow title='Date Fields' subtitle='Select date fields to be used as key facts'>
						<EditableMultipleSelect
							value={serviceTitanKeyFactSettings.enabledDateFields || []}
							onChange={handleDateFieldChange}
							isBusy={postExternalIntegrationKeyFactsSettingsMutation?.isPending}
							displayValue={
								(serviceTitanKeyFactSettings.enabledDateFields || []).length > 0
									? (serviceTitanKeyFactSettings.enabledDateFields || [])
											.map(fieldId => {
												return serviceTitanConfiguration.availableDateFields?.[fieldId] || fieldId;
											})
											.join(', ')
									: 'None selected'
							}
						>
							<MultipleSelectTrigger styles={styleSheet.editableField}>
								<MultipleSelectValue
									values={(serviceTitanKeyFactSettings.enabledDateFields || []).map(fieldId => {
										return serviceTitanConfiguration.availableDateFields?.[fieldId] || fieldId;
									})}
									placeholder='Select date fields'
								/>
							</MultipleSelectTrigger>
							<MultipleSelectContent>
								{Object.entries(serviceTitanConfiguration.availableDateFields || {}).map(([fieldName, displayName]) => {
									const isSelected = serviceTitanKeyFactSettings.enabledDateFields?.includes(fieldName);
									return (
										<MultipleSelectItem
											key={`service-titan-date-option-${fieldName}`}
											value={fieldName}
											selected={isSelected}
										>
											{displayName}
										</MultipleSelectItem>
									);
								})}
							</MultipleSelectContent>
						</EditableMultipleSelect>
					</SettingRow>
				</>
			) : null}

			{isSalesforceSettings && salesforceKeyFactSettings ? (
				<>
					<SettingRow title='Date Fields' subtitle='Select date fields to be used as key facts'>
						<EditableMultipleSelect
							value={salesforceKeyFactSettings.enabledDateFields || []}
							onChange={handleDateFieldChange}
							isBusy={postExternalIntegrationKeyFactsSettingsMutation?.isPending}
							displayValue={
								(salesforceKeyFactSettings.enabledDateFields || []).length > 0
									? (salesforceKeyFactSettings.enabledDateFields || [])
											.map(fieldId => {
												return salesforceConfiguration.availableDateFields?.[fieldId] || fieldId;
											})
											.join(', ')
									: 'None selected'
							}
						>
							<MultipleSelectTrigger styles={styleSheet.editableField}>
								<MultipleSelectValue
									values={(salesforceKeyFactSettings.enabledDateFields || []).map(fieldId => {
										return salesforceConfiguration.availableDateFields?.[fieldId] || fieldId;
									})}
									placeholder='Select date fields'
								/>
							</MultipleSelectTrigger>
							<MultipleSelectContent>
								{Object.entries(salesforceConfiguration.availableDateFields || {}).map(([fieldName, displayName]) => {
									const isSelected = salesforceKeyFactSettings.enabledDateFields?.includes(fieldName);
									return (
										<MultipleSelectItem key={fieldName} value={fieldName} selected={isSelected}>
											{displayName}
										</MultipleSelectItem>
									);
								})}
							</MultipleSelectContent>
						</EditableMultipleSelect>
					</SettingRow>
					<SettingRow title='Custom Date Fields' subtitle='Select custom date fields to be used as key facts'>
						<EditableMultipleSelect
							value={salesforceKeyFactSettings.enabledCustomDateFields || []}
							onChange={handleCustomFieldChange}
							isBusy={postExternalIntegrationKeyFactsSettingsMutation?.isPending}
							displayValue={getCustomDateFieldsDisplay(
								salesforceKeyFactSettings.enabledCustomDateFields,
								salesforceConfiguration
							)}
						>
							<MultipleSelectTrigger styles={styleSheet.editableField}>
								<MultipleSelectValue
									values={
										getCustomDateFieldsDisplayText(
											salesforceKeyFactSettings.enabledCustomDateFields,
											salesforceConfiguration
										) || []
									}
									placeholder='Select custom date fields'
								/>
							</MultipleSelectTrigger>
							<MultipleSelectContent>
								{Object.entries(salesforceConfiguration.availableCustomDateFields || {}).map(([id, text]) => {
									const isSelected = salesforceKeyFactSettings.enabledCustomDateFields?.includes(id);
									return (
										<MultipleSelectItem key={id} value={id} selected={isSelected}>
											{text}
										</MultipleSelectItem>
									);
								})}
							</MultipleSelectContent>
						</EditableMultipleSelect>
					</SettingRow>
				</>
			) : null}
		</SettingGroup>
	);
};
